import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import LandingPage from '@/pages/LandingPage';
import PropertyDetail from '@/pages/PropertyDetail';
import AdminLogin from '@/pages/AdminLogin';
import AdminDashboard from '@/pages/AdminDashboard';
import AnalyticsDashboard from '@/pages/AnalyticsDashboard';
import LineIntegration from '@/pages/LineIntegration';
import PropertyImport from '@/pages/PropertyImport';
import ClientRegister from '@/pages/ClientRegister';
import TeamPage from '@/pages/TeamPage';
import ServicesPage from '@/pages/ServicesPage';
import EducationPage from '@/pages/EducationPage';
import BlogManagement from '@/pages/BlogManagement';
import ServiceManagement from '@/pages/ServiceManagement';
import EmpireGroupPage from '@/pages/EmpireGroupPage';
import MediaPage from '@/pages/MediaPage';
import SocialImport from '@/pages/SocialImport';
import ClientLogin from '@/pages/ClientLogin';
import ContentManager from '@/pages/ContentManager';
import UserManagement from '@/pages/UserManagement';
import { Toaster } from '@/components/ui/sonner';
import { LanguageProvider } from '@/contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0E27]">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-solid border-[#D4AF37] border-r-transparent" />
      </div>
    );
  }

  return (
    <LanguageProvider>
      <AuthContext.Provider value={{ user, token, login, logout }}>
        <BrowserRouter>
          <div className="App">
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/property/:id" element={<PropertyDetail />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/education" element={<EducationPage />} />
              <Route path="/empire-group" element={<EmpireGroupPage />} />
              <Route path="/media" element={<MediaPage />} />
              <Route path="/register" element={<ClientRegister />} />
              <Route path="/login" element={<ClientLogin />} />
              <Route path="/admin/login" element={user ? <Navigate to="/admin/dashboard" /> : <AdminLogin />} />
              <Route path="/admin/dashboard" element={user ? <AdminDashboard /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/analytics" element={user ? <AnalyticsDashboard /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/blog" element={user ? <BlogManagement /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/services" element={user ? <ServiceManagement /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/integrations/line" element={user ? <LineIntegration /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/import" element={user ? <PropertyImport /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/social-import" element={user ? <SocialImport /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/content" element={user ? <ContentManager /> : <Navigate to="/admin/login" />} />
              <Route path="/admin/users" element={user ? <UserManagement /> : <Navigate to="/admin/login" />} />
            </Routes>
            <Toaster position="top-right" />
          </div>
        </BrowserRouter>
      </AuthContext.Provider>
    </LanguageProvider>
  );
}

export default App;